import React, { useState, useEffect, useCallback } from 'react';
import './Onboarding.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const STATUS_LABELS = {
  needs_api_key: 'Ждёт API-ключ',
  needs_nomenclature: 'Ждёт номенклатуру',
  needs_review: 'На проверке',
  ready: 'Готово',
};

const INVOICE_STATUS_LABELS = {
  pending: 'Ожидает оплаты',
  paid: 'Оплачен',
  cancelled: 'Отменён',
};

const th = { padding: '8px 6px' };
const td = { padding: '8px 6px' };

function formatDate(iso) {
  return iso ? new Date(iso).toLocaleDateString('ru-RU') : '—';
}

function PlatformAdmin({ token, onLogout }) {
  const [activeTab, setActiveTab] = useState('companies');
  const [companies, setCompanies] = useState([]);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState('pending');
  const [acts, setActs] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [expandedCompanyId, setExpandedCompanyId] = useState(null);
  const [usersByCompany, setUsersByCompany] = useState({});
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform/companies`, { headers: authHeaders });
      if (!res.ok) throw new Error('Не удалось получить список компаний');
      setCompanies(await res.json());
    } catch (e) {
      setError(e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform/settings`, { headers: authHeaders });
      if (!res.ok) throw new Error('Не удалось получить настройки платформы');
      setSettings(await res.json());
    } catch (e) {
      setError(e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchInvoices = useCallback(async (status) => {
    try {
      const url = status ? `${API_URL}/api/platform/invoices?status=${status}` : `${API_URL}/api/platform/invoices`;
      const res = await fetch(url, { headers: authHeaders });
      if (!res.ok) throw new Error('Не удалось получить список счетов');
      setInvoices(await res.json());
    } catch (e) {
      setError(e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchActs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform/acts`, { headers: authHeaders });
      if (!res.ok) throw new Error('Не удалось получить список актов');
      setActs(await res.json());
    } catch (e) {
      setError(e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  useEffect(() => { if (activeTab === 'settings' && !settings) fetchSettings(); }, [activeTab, settings, fetchSettings]);
  useEffect(() => { if (activeTab === 'invoices') fetchInvoices(invoiceFilter); }, [activeTab, invoiceFilter, fetchInvoices]);
  useEffect(() => { if (activeTab === 'acts') fetchActs(); }, [activeTab, fetchActs]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const toggleBlock = async (company) => {
    const action = company.is_blocked ? 'unblock' : 'block';
    try {
      const res = await fetch(`${API_URL}/api/platform/companies/${company.id}/${action}`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Не удалось изменить статус компании');
      await fetchCompanies();
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleTestFlag = async (company) => {
    try {
      const res = await fetch(`${API_URL}/api/platform/companies/${company.id}/set-test`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: !company.is_test }),
      });
      if (!res.ok) throw new Error('Не удалось изменить флаг «Тест»');
      await fetchCompanies();
    } catch (e) {
      setError(e.message);
    }
  };

  const changeOwnPassword = async (e) => {
    e.preventDefault();
    setChangingPassword(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/change-password`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPasswordInput }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Не удалось сменить пароль');
      showSuccess('Пароль изменён');
      setNewPasswordInput('');
      setShowPasswordForm(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setChangingPassword(false);
    }
  };

  const toggleUsersPanel = async (company) => {
    if (expandedCompanyId === company.id) {
      setExpandedCompanyId(null);
      return;
    }
    setExpandedCompanyId(company.id);
    if (!usersByCompany[company.id]) {
      try {
        const res = await fetch(`${API_URL}/api/platform/companies/${company.id}/users`, { headers: authHeaders });
        if (!res.ok) throw new Error('Не удалось получить пользователей компании');
        const users = await res.json();
        setUsersByCompany((prev) => ({ ...prev, [company.id]: users }));
      } catch (e) {
        setError(e.message);
      }
    }
  };

  const resetPassword = async (user) => {
    const newPassword = window.prompt(`Новый пароль для ${user.email} (мин. 6 символов, латиница+цифры, заглавная буква и цифра обязательны):`);
    if (!newPassword) return;
    try {
      const res = await fetch(`${API_URL}/api/platform/users/${user.id}/reset-password`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Не удалось сменить пароль');
      showSuccess(`Пароль для ${user.email} изменён`);
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteCompany = async (company) => {
    if (!company.is_test) {
      alert('Удалять можно только компании, отмеченные галочкой «Тест» — это защита от случайного удаления реального клиента. Поставьте галочку в таблице и повторите.');
      return;
    }
    const typed = window.prompt(
      `Это НЕОБРАТИМО удалит компанию «${company.name}» и ВСЕ её данные (пользователи, заказы, номенклатура, счета и т.д.).\n\n`
      + `Чтобы подтвердить, введите точное название компании:`
    );
    if (typed === null) return;
    if (typed.trim() !== company.name) {
      alert('Название не совпадает — удаление отменено.');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/platform/companies/${company.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Не удалось удалить компанию');
      showSuccess(`Компания «${company.name}» удалена`);
      await fetchCompanies();
    } catch (e) {
      setError(e.message);
    }
  };

  const setSeatOverride = async (company) => {
    const current = company.seat_override != null ? String(company.seat_override) : '';
    const input = window.prompt(
      `Ручной лимит мест для «${company.name}» — в обход обычной оплатной логики (например для Алессы). Оставьте пустым, чтобы снять оверрайд и вернуться к обычной логике:`,
      current
    );
    if (input === null) return; // отмена
    const trimmed = input.trim();
    const value = trimmed === '' ? null : parseInt(trimmed, 10);
    if (value !== null && (!Number.isInteger(value) || value < 1)) {
      alert('Введите целое число не меньше 1 или оставьте пустым, чтобы снять оверрайд');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/platform/companies/${company.id}/seat-override`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      });
      if (!res.ok) throw new Error('Не удалось сохранить оверрайд мест');
      await fetchCompanies();
    } catch (e) {
      setError(e.message);
    }
  };

  const openInvoicePdf = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/api/platform/invoices/${invoiceId}/pdf`, { headers: authHeaders });
      if (!res.ok) throw new Error('PDF счёта не найден');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) {
      setError(e.message);
    }
  };

  const generateInvoiceNow = async (company) => {
    if (!window.confirm(
      `Выставить счёт компании «${company.name}» сейчас, на период с ${formatDate(company.current_period_end)}? `
      + `Счёт уйдёт на почту ${company.billing_email || '(почта не указана)'}.`
    )) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/platform/companies/${company.id}/generate-invoice`, {
        method: 'POST',
        headers: authHeaders,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Не удалось создать счёт');
      showSuccess(`Счёт № ${data.number} создан${data.email_sent ? ' и отправлен на почту' : ' (письмо не отправлено — проверьте SMTP в настройках)'}`);
      if (activeTab === 'invoices') fetchInvoices(invoiceFilter);
    } catch (e) {
      setError(e.message);
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const res = await fetch(`${API_URL}/api/platform/settings`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      if (!res.ok) throw new Error('Не удалось сохранить настройки');
      showSuccess('Настройки сохранены');
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const openActPdf = async (actId) => {
    try {
      const res = await fetch(`${API_URL}/api/platform/acts/${actId}/pdf`, { headers: authHeaders });
      if (!res.ok) throw new Error('PDF акта не найден');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleActFlag = async (actId, kind, value) => {
    try {
      const res = await fetch(`${API_URL}/api/platform/acts/${actId}/${kind}`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ value }),
      });
      if (!res.ok) throw new Error('Не удалось сохранить отметку');
      await fetchActs();
    } catch (e) {
      setError(e.message);
    }
  };

  const markPaid = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/api/platform/invoices/${invoiceId}/mark-paid`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Не удалось отметить счёт оплаченным');
      await fetchInvoices(invoiceFilter);
      await fetchCompanies();
      showSuccess('Счёт отмечен оплаченным');
    } catch (e) {
      setError(e.message);
    }
  };

  const settingsField = (key, label, placeholder = '', type = 'text') => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, flex: 1 }}>
      {label}
      <input
        type={type}
        value={settings?.[key] || ''}
        placeholder={placeholder}
        onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
        style={{ padding: '7px 9px', border: '1px solid #d8dce3', borderRadius: 6, fontSize: 14 }}
      />
    </label>
  );

  return (
    <div className="onboarding-container">
      <div className="onboarding-card" style={{ maxWidth: 1200 }}>
        <div className="onboarding-header">
          <h1>🛠️ Платформенный админ</h1>
          <p>Управление компаниями и биллингом</p>
          <div style={{ position: 'absolute', top: 20, right: 20, display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => setShowPasswordForm((v) => !v)}
              style={{ background: 'none', border: '1px solid #ddd', borderRadius: 5, padding: '6px 12px', cursor: 'pointer' }}
            >
              {showPasswordForm ? 'Отмена' : 'Сменить пароль'}
            </button>
            <button
              type="button"
              onClick={onLogout}
              style={{ background: 'none', border: '1px solid #ddd', borderRadius: 5, padding: '6px 12px', cursor: 'pointer' }}
            >
              Выход
            </button>
          </div>
        </div>

        {showPasswordForm && (
          <form onSubmit={changeOwnPassword} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 16, padding: 12, background: '#f7f7fa', borderRadius: 8 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
              Новый пароль
              <input
                type="password"
                value={newPasswordInput}
                onChange={(e) => setNewPasswordInput(e.target.value)}
                required
                style={{ padding: '7px 9px', border: '1px solid #d8dce3', borderRadius: 6 }}
              />
            </label>
            <button
              type="submit"
              disabled={changingPassword}
              style={{ padding: '8px 16px', background: '#667eea', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
            >
              {changingPassword ? 'Сохранение...' : 'Сохранить'}
            </button>
          </form>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid #eee' }}>
          {[
            { key: 'companies', label: 'Компании' },
            { key: 'settings', label: 'Тарифы и реквизиты' },
            { key: 'invoices', label: 'Счета' },
            { key: 'acts', label: 'Акты' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                padding: '8px 14px', border: 'none', borderBottom: activeTab === t.key ? '2px solid #667eea' : '2px solid transparent',
                background: 'none', cursor: 'pointer', fontWeight: activeTab === t.key ? 600 : 400, fontSize: 14,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && <div className="onboarding-error">{error}</div>}
        {successMessage && <div style={{ background: '#d4edda', color: '#155724', padding: '10px 14px', borderRadius: 6, marginBottom: 12 }}>{successMessage}</div>}

        {activeTab === 'companies' && (
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 980 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                <th style={th}>Компания</th>
                <th style={th}>Статус</th>
                <th style={th}>Тариф</th>
                <th style={th}>Мест</th>
                <th style={th}>След. платёж</th>
                <th style={th}>Почта для счетов</th>
                <th style={th}>Создана</th>
                <th style={th} title="Освобождает компанию от автоблокировки за отбивку на (ненастоящую) почту для счетов">Тест</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => {
                const effectiveLimit = c.seat_override != null ? c.seat_override : c.active_seats;
                return (
                <React.Fragment key={c.id}>
                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={td}>{c.name}</td>
                  <td style={td}>
                    {c.is_blocked
                      ? `🚫 Заблокирована (${c.blocked_reason === 'unpaid' ? 'не оплачен счёт' : c.blocked_reason === 'bad_email' ? 'почта не существует' : 'вручную'})`
                      : (STATUS_LABELS[c.onboarding_status] || c.onboarding_status)}
                  </td>
                  <td style={td}>{c.billing_period === 'yearly' ? 'год' : 'месяц'}</td>
                  <td style={td}>
                    <div>{c.user_count} / {effectiveLimit}{c.seat_override != null && ' (оверрайд)'}</div>
                    {c.purchased_seats > effectiveLimit && (
                      <div style={{ fontSize: 11, color: '#e67e22' }}>запрошено {c.purchased_seats} — нужен новый счёт</div>
                    )}
                    <button
                      type="button"
                      onClick={() => setSeatOverride(c)}
                      style={{ marginTop: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer', borderRadius: 4, border: '1px solid #ddd', background: 'white', color: '#333' }}
                    >
                      Оверрайд
                    </button>
                  </td>
                  <td style={td}>{formatDate(c.current_period_end)}</td>
                  <td style={td}>{c.billing_email || '—'}</td>
                  <td style={td}>{formatDate(c.created_at)}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <input type="checkbox" checked={c.is_test} onChange={() => toggleTestFlag(c)} title="Тестовая компания — не блокировать за отбивку почты" />
                  </td>
                  <td style={{ ...td, display: 'flex', flexWrap: 'wrap', gap: 6, minWidth: 260 }}>
                    <button
                      type="button"
                      onClick={() => toggleBlock(c)}
                      style={{
                        padding: '6px 10px', fontSize: 13, cursor: 'pointer', borderRadius: 5,
                        border: '1px solid #ddd', background: c.is_blocked ? '#4caf50' : '#e53935',
                        color: 'white', whiteSpace: 'nowrap'
                      }}
                    >
                      {c.is_blocked ? 'Разблокировать' : 'Заблокировать'}
                    </button>
                    <button
                      type="button"
                      onClick={() => generateInvoiceNow(c)}
                      title={`Создать и отправить счёт на следующий период сейчас, не дожидаясь автоматической рассылки за 7 дней до ${formatDate(c.current_period_end)}. Срок оплаты в счёте останется прежним — ${formatDate(c.current_period_end)}. Полезно и для до-срочной докупки мест: если компания подняла желаемое число мест (видно в колонке «Мест»), этот счёт выставится уже на новое количество.`}
                      style={{ padding: '6px 10px', fontSize: 13, cursor: 'pointer', borderRadius: 5, border: '1px solid #ddd', background: 'white', color: '#333', whiteSpace: 'nowrap' }}
                    >
                      Выставить счёт досрочно
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleUsersPanel(c)}
                      style={{ padding: '6px 10px', fontSize: 13, cursor: 'pointer', borderRadius: 5, border: '1px solid #ddd', background: 'white', color: '#333', whiteSpace: 'nowrap' }}
                    >
                      {expandedCompanyId === c.id ? 'Скрыть пользователей' : 'Пользователи'}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteCompany(c)}
                      title={c.is_test ? 'Удалить компанию и все её данные безвозвратно' : 'Сначала поставьте галочку «Тест», чтобы разрешить удаление'}
                      style={{ padding: '6px 10px', fontSize: 13, cursor: 'pointer', borderRadius: 5, border: '1px solid #e53935', background: 'white', color: '#e53935', whiteSpace: 'nowrap' }}
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
                {expandedCompanyId === c.id && (
                  <tr style={{ borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
                    <td colSpan={9} style={{ padding: '10px 16px' }}>
                      {!usersByCompany[c.id] ? (
                        <span style={{ color: '#999' }}>Загрузка...</span>
                      ) : usersByCompany[c.id].length === 0 ? (
                        <span style={{ color: '#999' }}>Пользователей нет</span>
                      ) : (
                        <table style={{ width: '100%', fontSize: 13 }}>
                          <tbody>
                            {usersByCompany[c.id].map((u) => (
                              <tr key={u.id}>
                                <td style={{ padding: '4px 8px' }}>{u.email}</td>
                                <td style={{ padding: '4px 8px', color: '#777' }}>{u.role}</td>
                                <td style={{ padding: '4px 8px', color: '#777' }}>Вход: {u.last_login ? formatDate(u.last_login) : 'никогда'}</td>
                                <td style={{ padding: '4px 8px' }}>
                                  <button
                                    type="button"
                                    onClick={() => resetPassword(u)}
                                    style={{ padding: '4px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 5, border: '1px solid #ddd', background: 'white' }}
                                  >
                                    Сменить пароль
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
              {companies.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 20, textAlign: 'center', color: '#999' }}>Компаний пока нет</td></tr>
              )}
            </tbody>
          </table>
          </div>
        )}

        {activeTab === 'settings' && settings && (
          <form onSubmit={saveSettings} style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 640 }}>
            <h3 style={{ margin: '4px 0' }}>Тарифы</h3>
            <p style={{ margin: '0 0 4px', fontSize: 13, color: '#666' }}>
              База включает 1 пользователя компании; доплата за пользователя начисляется начиная со 2-го.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              {settingsField('monthly_base_price', 'База в месяц, ₽', 'например 8000')}
              {settingsField('monthly_price_per_user', 'За доп. пользователя в месяц, ₽', 'например 1000')}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {settingsField('yearly_base_price', 'База в год, ₽', 'например 80000')}
              {settingsField('yearly_price_per_user', 'За доп. пользователя в год, ₽', 'например 10000')}
            </div>

            <h3 style={{ margin: '12px 0 4px' }}>Реквизиты продавца (для печатной формы счёта)</h3>
            {settingsField('seller_name', 'Название / ИП')}
            <div style={{ display: 'flex', gap: 12 }}>
              {settingsField('seller_inn', 'ИНН')}
              {settingsField('seller_ogrn', 'ОГРН / ОГРНИП')}
            </div>
            {settingsField('seller_address', 'Адрес')}
            <div style={{ display: 'flex', gap: 12 }}>
              {settingsField('seller_bank_account', 'Расчётный счёт')}
              {settingsField('seller_bank_bik', 'БИК')}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {settingsField('seller_bank_name', 'Банк')}
              {settingsField('seller_bank_corr_account', 'Корр. счёт')}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {settingsField('seller_bank_inn', 'ИНН банка')}
              {settingsField('seller_bank_kpp', 'КПП банка')}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {settingsField('seller_email', 'Email')}
              {settingsField('seller_phone', 'Телефон')}
            </div>
            {settingsField('vat_note', 'Пометка по НДС', 'Без НДС')}

            <h3 style={{ margin: '12px 0 4px' }}>SMTP для отправки счетов клиентам</h3>
            <div style={{ display: 'flex', gap: 12 }}>
              {settingsField('system_smtp_host', 'SMTP host')}
              {settingsField('system_smtp_port', 'Порт', '587')}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {settingsField('system_smtp_user', 'Пользователь')}
              {settingsField('system_smtp_password', 'Пароль', '', 'password')}
            </div>
            {settingsField('system_smtp_from', 'From (если отличается от пользователя)', 'Имя отправителя <email@domain.ru> или просто email')}

            <button
              type="submit"
              disabled={savingSettings}
              style={{ alignSelf: 'flex-start', padding: '9px 18px', background: '#667eea', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
            >
              {savingSettings ? 'Сохранение...' : 'Сохранить'}
            </button>
          </form>
        )}

        {activeTab === 'invoices' && (
          <>
            <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
              {[
                { key: 'pending', label: 'Неоплаченные' },
                { key: '', label: 'Все' },
                { key: 'paid', label: 'Оплаченные' },
              ].map((f) => (
                <button
                  key={f.key || 'all'}
                  onClick={() => setInvoiceFilter(f.key)}
                  style={{
                    padding: '6px 12px', fontSize: 13, cursor: 'pointer', borderRadius: 5,
                    border: '1px solid #ddd', background: invoiceFilter === f.key ? '#667eea' : 'white',
                    color: invoiceFilter === f.key ? 'white' : '#333',
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                  <th style={th}>№</th>
                  <th style={th}>Компания</th>
                  <th style={th}>Период</th>
                  <th style={th}>Польз.</th>
                  <th style={th}>Сумма</th>
                  <th style={th}>Срок оплаты</th>
                  <th style={th}>Статус</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={td}>{inv.number}</td>
                    <td style={td}>{inv.company_name}</td>
                    <td style={td}>{formatDate(inv.period_start)} – {formatDate(inv.period_end)}</td>
                    <td style={td}>{inv.user_count ?? '—'}</td>
                    <td style={td}>{inv.amount != null ? `${inv.amount.toLocaleString('ru-RU')} ₽` : '—'}</td>
                    <td style={td}>{formatDate(inv.due_date)}</td>
                    <td style={td}>{INVOICE_STATUS_LABELS[inv.status] || inv.status}</td>
                    <td style={{ ...td, display: 'flex', gap: 8, alignItems: 'center' }}>
                      {inv.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => markPaid(inv.id)}
                          style={{ padding: '6px 12px', fontSize: 13, cursor: 'pointer', borderRadius: 5, border: '1px solid #4caf50', background: '#4caf50', color: 'white' }}
                        >
                          Отметить оплаченным
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openInvoicePdf(inv.id)}
                        style={{ padding: '6px 10px', fontSize: 13, cursor: 'pointer', borderRadius: 5, border: '1px solid #ddd', background: 'white' }}
                      >
                        PDF
                      </button>
                    </td>
                  </tr>
                ))}
                {invoices.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#999' }}>Счетов нет</td></tr>
                )}
              </tbody>
            </table>
          </>
        )}

        {activeTab === 'acts' && (
          <>
            <p style={{ fontSize: 13, color: '#666', margin: '0 0 12px' }}>
              Акт генерируется за 2 дня до конца оплаченного периода и приходит вам на почту.
              Подпишите его, отправьте клиенту и поставьте галочку «Отправлен» — тогда напоминания прекратятся.
              «Подписан» — для вашего учёта.
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                  <th style={th}>№ акта</th>
                  <th style={th}>Компания</th>
                  <th style={th}>Счёт</th>
                  <th style={th}>Период</th>
                  <th style={th}>Сумма</th>
                  <th style={th}>Отправлен</th>
                  <th style={th}>Подписан</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {acts.map((a) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={td}>{a.number}</td>
                    <td style={td}>{a.company_name}</td>
                    <td style={td}>{a.invoice_number}</td>
                    <td style={td}>{formatDate(a.period_start)} – {formatDate(a.period_end)}</td>
                    <td style={td}>{a.amount != null ? `${a.amount.toLocaleString('ru-RU')} ₽` : '—'}</td>
                    <td style={td}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={a.is_sent} onChange={(e) => toggleActFlag(a.id, 'mark-sent', e.target.checked)} />
                        {a.is_sent ? formatDate(a.sent_at) : <span style={{ color: '#e53935' }}>нет</span>}
                      </label>
                    </td>
                    <td style={td}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input type="checkbox" checked={a.is_signed} onChange={(e) => toggleActFlag(a.id, 'mark-signed', e.target.checked)} />
                        {a.is_signed ? formatDate(a.signed_at) : <span style={{ color: '#999' }}>нет</span>}
                      </label>
                    </td>
                    <td style={td}>
                      {a.has_pdf && (
                        <button type="button" onClick={() => openActPdf(a.id)} style={{ padding: '6px 10px', fontSize: 13, cursor: 'pointer', borderRadius: 5, border: '1px solid #ddd', background: 'white' }}>PDF</button>
                      )}
                    </td>
                  </tr>
                ))}
                {acts.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: '#999' }}>Актов пока нет — появятся за 2 дня до конца первого оплаченного периода</td></tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

export default PlatformAdmin;
